<template>
  <v-autocomplete
    color="black"
    item-color="primary"
    ref="productAutocomplete"
    v-model="chosenProduct"
    :label="label ?? $t('product')"
    item-text="name"
    item-value="name"
    return-object
    :clearable="clearable"
    :id="elementId"
    :loading="productsLoading"
    :items="products"
    :disabled="disabled"
    :item-disabled="item => disableScalable && !!item?.isScalable"
    :filter="(item, queryText, itemText) => itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1"
    :rules="rules"
    autocomplete="off"
    autocorrect="off"
    autocapitalize="off"
    spellcheck="false"
  >
    <template #item="{ item }">
      <v-tooltip v-if="disableScalable && item.isScalable" right color="info">
        <template #activator="{ on, attrs }">
          <span v-on="on" v-bind="attrs" style="display: block; width: 100%; pointer-events: auto; cursor: not-allowed;">{{ item.name }}</span>
        </template>
        <span class="subtitle-1 white--text" style="white-space: pre-line">{{ scalableTooltipText }}</span>
      </v-tooltip>
      <span v-else>{{ item.name }}</span>
    </template>
    <template #append-outer v-if="!hideRefresh">
      <v-icon :disabled="disabled" class="ml-n1 mr-1" @click="getProducts(true)" color="primary" tabindex="-1">mdi-refresh</v-icon>
      <slot name="append-outer"/>
    </template>
  </v-autocomplete>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { EnumValue, ContractType } from '@/utils/Enumerations'
import fieldRules from '@/utils/rules.js'

export default {
  name: 'ProductAutocomplete',

  props: {
    productId: { type: Number, default: undefined },
    disabled: { type: Boolean, default: false },
    contract: { type: Object, default: undefined },
    isDetailForm: { type: Boolean, default: false },
    woodType: {
      type: EnumValue,
      required: false,
      default: undefined
    },
    excludedProducts: { type: Array, default: function () { return [] } },
    includePreexistingProduct: { type: Boolean, default: true }, // Only keep preexisting product if contract hasn't changed (on a ticket)
    propFetchProducts: { type: Boolean, default: true },
    defaultToFirstProduct: { type: Boolean, default: false },
    elementId: { type: String, required: false, default: undefined },
    required: { type: Boolean, required: false, default: false },
    limitToScalingSpecs: { type: Boolean, required: false, default: false },
    label: { type: String, required: false, default: undefined },
    hideRefresh: { type: Boolean, required: false, default: false },
    clearable: { type: Boolean, required: false, default: true },
    filteredProductIds: { type: Array, default: undefined },
    limitToContractProducts: { type: Boolean, default: true },
    disableScalable: { type: Boolean, default: false },
    disabledScalableTooltip: { type: String, required: false, default: undefined }
  },

  data: () => ({
    chosenProduct: undefined
  }),

  watch: {
    chosenProduct: {
      handler (val) {
        this.$emit('product-chosen', val)
      },
      deep: true
    },

    products (products) {
      if (this.chosenProduct && products.every(product => product?.productId !== this.chosenProduct?.productId)) this.chosenProduct = null
    }
  },

  computed: {
    ...mapGetters('product', ['allProducts', 'productsLoading']),
    productRequestObj () {
      const isByproducts = this.contract?.type === ContractType.ByproductSale.value || this.contract?.type === ContractType.ByproductPurchase.value || this.contract?.type === ContractType.ByproductHauling.value
      return { includeByProducts: isByproducts, includeProduction: !isByproducts }
    },

    products () {
      if (!this.allProducts) return []

      const products = this.allProducts
        .filter(this.productMeetsRequirements)
        .map(p => ({
          ...p,
          sortOrder: p.sortOrder ?? this.contract?.products?.find(cp => cp.productId === p.productId)?.sortOrder ?? 0
        }))
        .sort((a, b) => a.sortOrder - b.sortOrder)

      if (this.productId !== -1 &&
        !!this.productId &&
        !products.some(p => p.productId === this.productId) &&
        this.includePreexistingProduct
      ) {
        products.push(this.allProducts.find(p => p.productId === this.productId))
      }

      return products
    },

    rules () {
      return this.required ? [fieldRules.rules.required] : []
    },

    scalableTooltipText () {
      return this.disabledScalableTooltip ?? this.$t('scalableProductsNotEligible')
    }
  },

  async created () {
    await this.getProducts()
    const chosenProduct = this.allProducts.find(p => p.productId === this.productId)
    if (chosenProduct) {
      this.chosenProduct = chosenProduct
    } else if (this.defaultToFirstProduct) {
      this.chosenProduct = this.products[0]
    }
  },

  methods: {
    ...mapActions('product', ['fetchProducts']),

    focus () {
      if (this.$refs.productAutocomplete) this.$refs.productAutocomplete.focus()
    },

    getContractId () {
      if (this.contract === undefined) { return }
      if (this.isDetailForm === true) { return }
      if (this.contract.acceptsAnyLoad) { return }
      if (this.limitToContractProducts === false) { return }
      return this.contract.contractId
    },

    async getProducts (forceFetch = false) {
      if (forceFetch || this.propFetchProducts) await this.fetchProducts({ ...this.productRequestObj, contractId: this.getContractId() })
    },

    productMeetsRequirements (product) {
      return this.productMeetsWoodTypeRequirement(product) &&
        this.productIsNotExcluded(product) &&
        this.productMeetsContractRequirement(product) &&
        this.productMeetsScalingStatsRequirement(product) &&
        this.productMeetsFilteredIdsRequirement(product)
    },

    productMeetsWoodTypeRequirement (product) { return this.woodType === undefined || product.woodType === this.woodType.value },

    productIsNotExcluded  (product) { return !this.excludedProducts.includes(product.name) || this.productId === product.productId },

    productMeetsContractRequirement (product) { // Product is either on contract or no contract products were supplied
      const targetProductId = product.productId
      const contract = this.contract
      if (!contract?.products) return true
      if (!this.getContractId()) return true
      return contract.products?.some(p => p.productId === targetProductId)
    },

    productMeetsScalingStatsRequirement (product) {
      return !this.limitToScalingSpecs || product.isScalable
    },

    productMeetsFilteredIdsRequirement (product) {
      return this.filteredProductIds === undefined || this.filteredProductIds.includes(product.productId)
    }
  }
}
</script>
